import PropTypes from 'prop-types';
import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';
import Datetime from 'react-datetime';
import moment from 'moment';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import withStyles from '@material-ui/core/styles/withStyles';
import { get } from 'helpers/apiHelpers';
import { ROLE_SHOW_SUB_BRAND } from 'helpers/roles';
import axios from 'helpers/gastro';
import { combineStyles, isGranted } from 'helpers/helpers';
import { isDatepickerValidDay } from 'helpers/dateHelpers';
import getSelectSubBrandLabel from 'helpers/getSelectSubBrandLabel';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import SelectAll from 'components/SelectAll';
import ReportConfigurator from 'components/Report/ReportConfigurator';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import Archive from 'components/Archive/Archive';
import MultiplierCalculator from 'components/MultiplierCalculator/MultiplierCalculator';
import CardWrapper from 'components/Card/CardWrapper';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx';
import AccessDenied from 'components/Security/AccessDenied';
import { toast } from 'react-toastify';
import { apiErrorToast } from 'utils/apiError';
import DEFAULT_SALES_MARKET_LANG from 'constants/defaultSalesMarketLang';

import DownloadReportButtons from './DownloadReportButtons/DownloadReportButtons';
import MIME_TYPES from './constants/mimeTypes';
import ReportPreview from './ReportPreviev/ReportPreview';

class BrandDateSubbrandsReport extends Component {
  state = {
    selectedBrands: [],
    subBrands: [],
    selectedSubBrands: [],
    date: '',
    dateFrom: '',
    dateTo: '',
    multiplier: 1,
    isGenerating: false,
    isGeneratingPreview: false,
    excelContent: {},
    multiplierCalculatorOpen: false,
    isReportPreviewOpen: false,
  };

  handleChange = event => {
    this.setState({
      [event.target.name]: event.target.value,
    });
  };

  validateDate = () =>
    this.props.useDateRange
      ? this.state.dateFrom && this.state.dateTo
      : this.state.date;

  validateBrands = () => {
    if (this.props.validateBrands) {
      return this.state.selectedBrands.length !== 0;
    }

    return true;
  };

  fetchSubBrands = async selected => {
    const selectInBrands = selected.length
      ? selected.map(brand => brand.value).filter(val => val !== '*')
      : [];

    const params = {
      pagination: false,
    };

    const { useBrands = true } = this.props;

    if (selectInBrands.length > 0 && useBrands) {
      params.selectInBrands = selectInBrands;
    }

    const response = await get('/sub-brands', params);

    const allSubBrandsElement = this.state.selectedSubBrands.find(
      subBrand => subBrand.value === '*'
    );

    const subBrands = response['hydra:member'];

    const selectedSubBrands = allSubBrandsElement
      ? subBrands.map(({ name: label, '@id': value }) => ({ label, value }))
      : [];

    return this.setState({
      subBrands,
      selectedSubBrands: [allSubBrandsElement, ...selectedSubBrands],
    });
  };

  componentDidMount() {
    if (isGranted(ROLE_SHOW_SUB_BRAND)) {
      this.fetchSubBrands([]);
    }
  }

  getRequestParams = () => ({
    ...(this.props.useBrands && {
      brands: this.state.selectedBrands.map(brand => brand.value),
    }),
    ...(this.props.useSubBrands && {
      subBrands:
        this.state.selectedSubBrands.map(subBrand => subBrand?.value) || null,
    }),
    ...(this.props.showMultiplier && {
      multiplier: this.state.multiplier,
    }),
    ...(this.props.useDateRange
      ? {
          dateFrom: this.state.dateFrom,
          dateTo: this.state.dateTo,
        }
      : { date: this.state.date }),
    ...this.props.additionalParams,
  });

  // eslint-disable-next-line consistent-return -- FIXME
  handleGenerate = (mimeType, lang) => {
    this.setState({ isGenerating: true });
    if (!this.validateDate()) {
      this.setState({ isGenerating: false });
      return toast.error(this.props.t('reports.sDate'));
    }
    if (!this.validateBrands()) {
      this.setState({ isGenerating: false });
      return toast.error(this.props.t('reports.selectBrand'));
    }

    const { eachRecipeOnNewPage } = this.props;
    let additionalParams = {};
    if (typeof this.props.additionalParams === 'object') {
      additionalParams = this.props.additionalParams;
    }

    if (typeof eachRecipeOnNewPage === 'boolean') {
      additionalParams.eachRecipeOnNewPage = eachRecipeOnNewPage;
    }

    axios
      .get(this.props.url, {
        responseType: 'blob',
        params: this.getRequestParams(),
        headers: { accept: mimeType },
        forcedLanguage: lang,
      })
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(
        response => {
          if (response.data) {
            const url = window.URL.createObjectURL(new Blob([response.data]));

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
              'download',
              `${this.props.fileName(this.state)}.xlsx`
            );

            document.body.appendChild(link);
            link.click();
            this.setState({ isGenerating: false });
          } else {
            alert(this.props.t('reports.noAccess'));
          }
        },
        // eslint-disable-next-line promise/prefer-await-to-callbacks -- FIXME
        error => {
          apiErrorToast(this.props.t('reports.cannotGenerate'), error);
          this.setState({ isGenerating: false });
        }
      )
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .finally(() => {
        this.setState({ isGenerating: false });
      });
  };

  // eslint-disable-next-line consistent-return -- FIXME
  handlePreviewGenerate = (mimeType, lang) => {
    this.setState({ isGeneratingPreview: true });

    if (!this.validateDate()) {
      this.setState({ isGeneratingPreview: false });
      return toast.error(this.props.t('reports.sDatePreview'));
    }

    if (!this.validateBrands()) {
      this.setState({ isGeneratingPreview: false });
      return toast.error(this.props.t('reports.selectBrand'));
    }

    const { eachRecipeOnNewPage } = this.props;

    let additionalParams = {};
    if (typeof this.props.additionalParams === 'object') {
      additionalParams = this.props.additionalParams;
    }

    if (typeof eachRecipeOnNewPage === 'boolean') {
      additionalParams.eachRecipeOnNewPage = eachRecipeOnNewPage;
    }

    axios
      .get(this.props.url, {
        responseType: 'blob',
        params: this.getRequestParams(),
        headers: { accept: MIME_TYPES.json },
        forcedLanguage: lang,
      })
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(
        async response => {
          if (response.data) {
            const text = await response.data.text();
            const excelContent = JSON.parse(text);

            this.setState({
              excelContent,
              isGeneratingPreview: false,
              isReportPreviewOpen: true,
            });
          } else {
            alert(this.props.t('reports.noAccess'));
          }
        },
        // eslint-disable-next-line promise/prefer-await-to-callbacks -- FIXME
        error => {
          apiErrorToast(this.props.t('reports.cannotGeneratePreview'), error);
          this.setState({ isGeneratingPreview: false });
        }
      )
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .finally(() => {
        this.setState({ isGeneratingPreview: false });
      });
  };

  render() {
    const {
      additionalParams,
      archiveReportNames,
      brands,
      classes,
      company,
      dateType,
      reportConfiguration,
      showMultiplier,
      t,
      title,
      useBrands = true,
      useDateRange,
      useSubBrands = true,
    } = this.props;

    const {
      date,
      dateFrom,
      dateTo,
      excelContent,
      isReportPreviewOpen,
      multiplier,
      multiplierCalculatorOpen,
      selectedBrands,
      selectedSubBrands,
      subBrands,
      isGenerating,
      isGeneratingPreview,
    } = this.state;

    // eslint-disable-next-line radix -- FIXME
    const companyId = parseInt(company.split('/').pop());
    const reportKey = reportConfiguration?.report || false;
    const fields = reportConfiguration?.fields;

    return isGranted(this.props.role) ? (
      <>
        <CardWrapper
          title={
            <>
              {title}
              {reportKey && (
                <ReportConfigurator
                  companyId={companyId}
                  report={reportKey}
                  fields={fields}
                />
              )}
            </>
          }
          style={{ marginTop: '20px' }}
        >
          <Dialog open={isGenerating || isGeneratingPreview}>
            <DialogContent>
              <div style={{ textAlign: 'center' }}>
                <h1>
                  {isGeneratingPreview
                    ? t('reports.generatePreview.loader')
                    : t('reports.generate')}
                </h1>
                <CircularProgress />
              </div>
            </DialogContent>
          </Dialog>

          <CardBody>
            <GridContainer justify="flex-start">
              {useBrands && (
                <GridItem
                  sm={showMultiplier ? 2 : 4}
                  data-cy="report-brand-select"
                >
                  <FormLabel className={classes.labelHorizontal}>
                    {t('reports.selectBrands')} *
                  </FormLabel>
                  <SelectAll
                    className="input-select--brand"
                    options={brands}
                    trackBy="@id"
                    mapBy="name"
                    optionSelected={selectedBrands}
                    handleChange={selected => {
                      this.setState({
                        selectedBrands: selected,
                        subBrands: [],
                        selectedSubBrands: [],
                      });
                      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- FIXME
                      isGranted(ROLE_SHOW_SUB_BRAND) &&
                        this.fetchSubBrands(selected);
                    }}
                  />
                </GridItem>
              )}
              {showMultiplier && (
                <GridItem sm={2}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('common.multiplier')}
                  </FormLabel>
                  <FormControl fullWidth={true}>
                    <FormTextInput
                      className="input-text--multiplier"
                      classes={classes}
                      name="multiplier"
                      value={multiplier}
                      type="number"
                      handleChange={this.handleChange}
                      inputSize={12}
                      onFocus={() =>
                        this.setState({ multiplierCalculatorOpen: true })
                      }
                      inputRef={node => {
                        this.anchorElement = node;
                      }}
                    />
                    <MultiplierCalculator
                      classes={classes}
                      url="/statistics/ecommerce/bags/day"
                      open={multiplierCalculatorOpen}
                      anchorElement={this.anchorElement}
                      brandIds={selectedBrands}
                      handleChange={(name, value) =>
                        this.handleChange({
                          target: {
                            name,
                            value,
                          },
                        })
                      }
                    />
                  </FormControl>
                </GridItem>
              )}
              {isGranted(ROLE_SHOW_SUB_BRAND) && useSubBrands && (
                <GridItem sm={4} data-cy="report-subbrand-select">
                  <FormLabel className={classes.labelHorizontal}>
                    {t('reports.selectSubbrands')}
                  </FormLabel>
                  <SelectAll
                    className="input-select--subbrand"
                    options={subBrands}
                    trackBy="@id"
                    mapBy="name"
                    optionSelected={selectedSubBrands}
                    customLabel={subBrand =>
                      getSelectSubBrandLabel(subBrand, brands)
                    }
                    handleChange={selected => {
                      this.setState({
                        selectedSubBrands: selected,
                      });
                    }}
                  />
                </GridItem>
              )}
              {this.props.useDateRange && (
                <>
                  <GridItem sm={useSubBrands ? 2 : 4}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('reports.deliveryDateFrom')} *
                    </FormLabel>
                    <FormControl fullWidth={true}>
                      <Datetime
                        className="input-datetime--from"
                        isValidDate={isDatepickerValidDay}
                        timeFormat={false}
                        dateFormat={moment.localeData().longDateFormat('L')}
                        closeOnSelect={true}
                        // eslint-disable-next-line new-cap -- FIXME
                        value={new moment(this.state.dateFrom)}
                        onChange={ev =>
                          this.setState({ dateFrom: ev.format('YYYY-MM-DD') })
                        }
                        inputProps={{
                          readOnly: true,
                          'data-cy': 'report-date-from',
                        }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem sm={useSubBrands ? 2 : 4}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('reports.deliveryDateTo')} *
                    </FormLabel>
                    <FormControl fullWidth={true}>
                      <Datetime
                        className="input-datetime--date-to"
                        isValidDate={isDatepickerValidDay}
                        timeFormat={false}
                        dateFormat={moment.localeData().longDateFormat('L')}
                        closeOnSelect={true}
                        // eslint-disable-next-line new-cap -- FIXME
                        value={new moment(dateTo)}
                        onChange={e =>
                          this.setState({ dateTo: e.format('YYYY-MM-DD') })
                        }
                        inputProps={{
                          readOnly: true,
                          'data-cy': 'report-date-to',
                        }}
                      />
                    </FormControl>
                  </GridItem>
                </>
              )}
              {!useDateRange && (
                <>
                  <GridItem sm={useSubBrands ? 4 : 8}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('reports.selectDate')} *
                    </FormLabel>
                    <FormControl fullWidth={true}>
                      <Datetime
                        className="input-datetime--date"
                        isValidDate={isDatepickerValidDay}
                        timeFormat={false}
                        dateFormat={moment.localeData().longDateFormat('L')}
                        closeOnSelect={true}
                        // eslint-disable-next-line new-cap -- FIXME
                        value={new moment(this.state.date)}
                        onChange={ev =>
                          this.setState({ date: ev.format('YYYY-MM-DD') })
                        }
                        inputProps={{
                          readOnly: true,
                          'data-cy': 'report-date',
                        }}
                      />
                    </FormControl>
                  </GridItem>
                </>
              )}
              {this.props.children}
            </GridContainer>
            <GridContainer justify="flex-end">
              <GridItem>
                <DownloadReportButtons
                  isSalesMarketLangViewEnabled={
                    this.props.isSalesMarketLangViewEnabled
                  }
                  previewXlsxAvailable={this.props.previewXlsxAvailable}
                  handlePreviewGenerate={this.handlePreviewGenerate}
                  mimeTypes={[this.props.mimeType]}
                  handleGenerate={this.handleGenerate}
                  areButtonsDisabled={this.state.isGenerating}
                />
              </GridItem>
            </GridContainer>
          </CardBody>
        </CardWrapper>
        <Card>
          <CardBody>
            <Archive
              options={{
                brands,
                subBrands,
                ...this.props.additionalOptions,
              }}
              type={['REPORT']}
              reportName={archiveReportNames}
            />
          </CardBody>
        </Card>
        <ReportPreview
          date={date}
          dateFrom={dateFrom}
          dateTo={dateTo}
          dateType={dateType}
          excelContent={excelContent}
          groupClient={additionalParams?.groupClient}
          includeShopElements={additionalParams?.includeShopElements}
          includeSubscriptions={additionalParams?.includeSubscriptions}
          isOpen={isReportPreviewOpen}
          multiplier={multiplier}
          packedDate={additionalParams?.packedDate}
          selectedBrands={selectedBrands}
          selectedSubBrands={selectedSubBrands}
          shouldBeConsumedTo={additionalParams?.shouldBeConsumedTo}
          title={title}
          useSubBrands={useSubBrands}
          downloadXls={() => {
            this.handleGenerate(MIME_TYPES.xlsx, DEFAULT_SALES_MARKET_LANG);
          }}
          onClose={() => {
            this.setState({ isReportPreviewOpen: false });
          }}
        />
      </>
    ) : (
      <AccessDenied />
    );
  }
}

const combinedStyles = combineStyles(extendedFormsStyle, buttonsStyle);

const mapStateToProps = state => ({
  brands: state.Auth.user.brands,
  company: state.Auth.user.company,
});

BrandDateSubbrandsReport.propTypes = {
  brands: PropTypes.array,
  classes: PropTypes.any,
  archiveReportNames: PropTypes.arrayOf(PropTypes.string),
  fileName: PropTypes.func.isRequired,
  showMultiplier: PropTypes.bool,
  validateBrands: PropTypes.bool,
  useDateRange: PropTypes.bool,
  useSubBrands: PropTypes.bool,
  mimeType: PropTypes.string.isRequired,
  title: PropTypes.string,
  url: PropTypes.string.isRequired,
  isSalesMarketLangViewEnabled: PropTypes.bool,
};

BrandDateSubbrandsReport.defaultProps = {
  isSalesMarketLangViewEnabled: false,
};

const enhance = compose(
  connect(mapStateToProps, null),
  withStyles(combinedStyles),
  withTranslation()
);

export default enhance(BrandDateSubbrandsReport);
